/**
 * Variant Selection Fix - Shopify Product JSON Method
 *
 * Works by fetching full product data from Shopify's .json endpoint
 * and mapping option values to variant IDs.
 *
 * This fix works even when:
 * - Theme's JavaScript is broken
 * - No data-product-url attributes exist
 * - No variant-selects custom elements
 */
(function() {
  'use strict';

  const DEBUG = window.location.search.includes('variant_debug=true');

  function log(...args) {
    if (DEBUG) {
      console.log('[Variant Fix]', ...args);
    }
  }

  log('=== Variant Fix Loading ===');

  let productData = null;
  let imageUpdateTimeout = null;

  // Fetch product data from Shopify's JSON endpoint
  async function fetchProductData() {
    try {
      // Get product URL from product-info element or current page
      const productInfo = document.querySelector('product-info');
      let productUrl = productInfo?.dataset.url;

      // Validate the URL before using pathname
      if (!productUrl) {
        productUrl = window.location.pathname;

        // Ensure we have a valid product URL
        if (!productUrl.includes('/products/')) {
          log('Invalid product URL:', productUrl);

          // Try to find product URL from a form or link
          const productLink = document.querySelector('a[href*="/products/"]');
          if (productLink) {
            const url = new URL(productLink.href);
            productUrl = url.pathname;
            log('Found product URL from link:', productUrl);
          } else {
            throw new Error('Cannot determine product URL');
          }
        }
      }

      // Remove any query params, hash and ensure it ends with .json
      productUrl = productUrl.split('?')[0].split('#')[0];
      if (!productUrl.endsWith('.json')) {
        productUrl += '.json';
      }

      log('Fetching product data from:', productUrl);

      const response = await fetch(productUrl);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      productData = data.product;
      log('Product data loaded:', productData.title);
      log('Variants:', productData.variants.length);

      return productData;
    } catch (error) {
      console.error('[Variant Fix] Failed to fetch product data:', error);
      return null;
    }
  }

  // Find variant ID based on selected options
  function findVariantByOptions(selectedOptions) {
    if (!productData || !productData.variants) {
      log('No product data available');
      return null;
    }

    log('Looking for variant matching:', selectedOptions);

    // Find variant that matches all selected options
    const variant = productData.variants.find(v => {
      // For single option products
      if (selectedOptions.length === 1) {
        return v.option1 === selectedOptions[0];
      }
      // For multi-option products
      if (selectedOptions.length === 2) {
        return v.option1 === selectedOptions[0] && v.option2 === selectedOptions[1];
      }
      if (selectedOptions.length === 3) {
        return v.option1 === selectedOptions[0] &&
               v.option2 === selectedOptions[1] &&
               v.option3 === selectedOptions[2];
      }
      return false;
    });

    if (variant) {
      log('Found variant:', variant.title, 'ID:', variant.id);
      return variant;
    } else {
      log('WARNING: No variant found for options:', selectedOptions);
      return null;
    }
  }

  // Get currently selected options from all select elements
  function getSelectedOptions() {
    const selects = document.querySelectorAll('select[name*="options"]');
    const options = [];

    selects.forEach(select => {
      if (select.value) {
        options.push(select.value);
      }
    });

    log('Selected options:', options);
    return options;
  }

  // Update variant ID in all forms
  function updateVariantId(variantId) {
    log('Updating forms with variant ID:', variantId);

    const forms = document.querySelectorAll('form[action*="/cart/add"]');
    let updated = 0;

    forms.forEach(form => {
      const input = form.querySelector('input[name="id"]');
      if (input) {
        const oldValue = input.value;
        input.value = variantId;
        input.disabled = false;

        // Dispatch change event
        input.dispatchEvent(new Event('change', { bubbles: true }));

        log('Updated form:', form.id || 'unnamed', oldValue, '->', variantId);
        updated++;
      }
    });

    if (updated === 0) {
      log('WARNING: No forms were updated!');
    } else {
      log(`✅ Updated ${updated} form(s)`);
    }

    return updated > 0;
  }

  // Safe scrollIntoView with browser compatibility fallback
  function safeScrollIntoView(element) {
    if (!element) return;

    try {
      // Test if browser supports smooth scrolling
      if ('scrollBehavior' in document.documentElement.style) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'start'
        });
      } else {
        // Fallback for older browsers (instant scroll)
        element.scrollIntoView(false);
      }
    } catch (error) {
      // Ultimate fallback for ancient browsers
      log('ScrollIntoView failed, using manual scroll:', error);
      if (element.parentElement) {
        element.parentElement.scrollLeft = element.offsetLeft;
      }
    }
  }

  /**
   * Update gallery to show a specific media item
   * @param {HTMLElement} gallery - The media-gallery element
   * @param {string} targetMediaId - Full media ID (e.g., "template--123-45678")
   * @returns {boolean} - True if gallery was updated
   */
  function setActiveMediaInGallery(gallery, targetMediaId) {
    if (!gallery || !targetMediaId) {
      log('setActiveMediaInGallery: Invalid parameters');
      return false;
    }

    const galleryId = gallery.id;

    // Find the target media item
    const targetMediaItem = gallery.querySelector(`[data-media-id="${targetMediaId}"]`);

    if (!targetMediaItem) {
      log('Gallery %s: Media item not found for ID %s', galleryId, targetMediaId);
      return false;
    }

    // Remove is-active from all media items in this gallery
    gallery.querySelectorAll('.product__media-item.is-active').forEach(item => {
      item.classList.remove('is-active');
    });

    // Add is-active to target media item
    targetMediaItem.classList.add('is-active');

    // Update thumbnail highlighting (optional feature, may not exist on all layouts)
    const thumbnailList = gallery.querySelector('.thumbnail-list');
    if (thumbnailList) {
      thumbnailList.querySelectorAll('button').forEach(thumb => {
        thumb.removeAttribute('aria-current');
      });

      const targetThumbnail = gallery.querySelector(
        `.thumbnail-list__item[data-target="${targetMediaId}"] button`
      );
      if (targetThumbnail) {
        targetThumbnail.setAttribute('aria-current', 'true');
      }
    }

    // Scroll the media item into view (for slider layout)
    const sectionId = galleryId.replace('MediaGallery-', '');
    const slider = gallery.querySelector('#Slider-Gallery-' + sectionId);
    if (slider) {
      const mediaIndex = Array.from(slider.children).indexOf(targetMediaItem);
      if (mediaIndex >= 0) {
        safeScrollIntoView(targetMediaItem);
      }
    }

    log('✅ Updated gallery %s to show media %s', galleryId, targetMediaId);
    return true;
  }

  // Update product images based on variant's featured image
  function updateProductImages(variant) {
    if (!variant) {
      log('No variant provided, skipping image update');
      return false;
    }

    // Find all media gallery sections
    const mediaGalleries = document.querySelectorAll('media-gallery');
    let updated = 0;

    mediaGalleries.forEach(gallery => {
      const galleryId = gallery.id;
      log('Gallery %s: Looking for image for variant "%s"', galleryId, variant.title);

      const allMediaItems = gallery.querySelectorAll('[data-media-id]');
      const thumbnails = gallery.querySelectorAll('.thumbnail-list__item');
      let targetMediaId = null;

      // Early validation
      if (thumbnails.length === 0) {
        log('Gallery %s: No thumbnails found - skipping', galleryId);
        return;
      }

      // APPROACH 1: Try using variant's featured_image (Shopify standard property)
      // Note: Shopify uses "featured_image" for variants, not "featured_media"
      const featuredImage = variant.featured_image || variant.featured_media;

      if (featuredImage) {
        const featuredImageId = featuredImage.id;
        log('Gallery %s: Variant has featured_image ID: %s', galleryId, featuredImageId);

        // Search for media item whose ID ends with the featured image ID
        for (const item of allMediaItems) {
          const itemMediaId = item.getAttribute('data-media-id');
          if (itemMediaId && itemMediaId.endsWith('-' + featuredImageId)) {
            targetMediaId = itemMediaId;
            log('Gallery %s: ✅ Found media via featured_image: %s', galleryId, targetMediaId);
            break;
          }
        }
      } else {
        log('Gallery %s: Variant has no featured_image, will try fallback approaches', galleryId);
      }

      // APPROACH 2: Fallback - use variant.image_id or product.images variant_ids array
      // When images are manually assigned in Shopify, both properties are set and point to the same image
      if (!targetMediaId) {
        let imageIdToFind = null;

        // Sub-approach 2a: Direct variant.image_id (faster, most common)
        if (variant.image_id && allMediaItems.length > 0) {
          imageIdToFind = variant.image_id;
          log('Gallery %s: Trying variant.image_id matching - %s', galleryId, imageIdToFind);
        }

        // Sub-approach 2b: product.images variant_ids array (fallback)
        if (!imageIdToFind && productData && productData.images && productData.images.length > 0) {
          log('Gallery %s: Trying product.images variant_ids matching for variant ID %s', galleryId, variant.id);

          const imageForVariant = productData.images.find(img =>
            img.variant_ids && img.variant_ids.includes(variant.id)
          );

          if (imageForVariant) {
            imageIdToFind = imageForVariant.id;
            log('Gallery %s: Found image ID %s via variant_ids (has %d variants assigned)',
                galleryId, imageIdToFind, imageForVariant.variant_ids.length);
          } else {
            log('Gallery %s: No image found with variant ID %s in variant_ids array', galleryId, variant.id);
          }
        }

        // Search for media item with the image ID (from either sub-approach)
        if (imageIdToFind && thumbnails.length > 0) {
          log('Gallery %s: Searching for image ID %s via thumbnails', galleryId, imageIdToFind);

          // Find the image data in product.images
          if (productData && productData.images) {
            const imageData = productData.images.find(img => img.id === imageIdToFind);

            if (imageData) {
              log('Gallery %s: Found image data for ID %s', galleryId, imageIdToFind);
              log('Gallery %s: Image src: %s', galleryId, imageData.src.split('/').pop());

              // Match by image src - find thumbnail whose img src contains the same filename
              // Shopify thumbnails use the same base image with size variants (_100x, _200x, etc)
              // Strip query params and size variants to get the base filename for comparison
              const imageFilename = imageData.src.split('?')[0].split('/').pop();
              const imageBasename = imageFilename.replace(/_\d+x\d*\./, '.'); // Remove _100x, _200x150, etc

              // Cache thumbnail basenames for faster comparison
              if (!gallery.__thumbnailCache) {
                gallery.__thumbnailCache = new Map();
                thumbnails.forEach(thumbnail => {
                  const thumbImg = thumbnail.querySelector('img');
                  const thumbSrc = thumbImg?.getAttribute('src') || '';
                  if (thumbSrc) {
                    const thumbFilename = thumbSrc.split('?')[0].split('/').pop();
                    const thumbBasename = thumbFilename.replace(/_\d+x\d*\./, '.');
                    gallery.__thumbnailCache.set(thumbnail, thumbBasename);
                  }
                });
              }

              for (const thumbnail of thumbnails) {
                const thumbBasename = gallery.__thumbnailCache.get(thumbnail);

                if (!thumbBasename) continue;

                // Match if basenames are the same
                if (imageBasename === thumbBasename) {
                  targetMediaId = thumbnail.getAttribute('data-target');
                  if (targetMediaId) {
                    log('Gallery %s: ✅ Found media via image src match: %s', galleryId, targetMediaId);
                    log('Gallery %s: Matched %s to %s', galleryId, imageBasename, thumbBasename);
                    break;
                  }
                }
              }

              if (!targetMediaId) {
                log('Gallery %s: Image src %s not found in any thumbnail',
                    galleryId, imageData.src.split('/').pop());
              }
            } else {
              log('Gallery %s: No image data found for image_id %s', galleryId, imageIdToFind);
            }
          }
        }
      }

      // APPROACH 3: Last resort - find thumbnail by matching variant option in image alt/src
      if (!targetMediaId && thumbnails.length > 0) {
        // Get variant's first option (usually the differentiating factor like "Hello Kitty", "Kuromi")
        const variantOption = variant.option1;

        if (variantOption) {
          const optionLower = variantOption.toLowerCase();
          log('Gallery %s: Trying option-based matching - searching for "%s" in %d thumbnails',
              galleryId, variantOption, thumbnails.length);

          // Search thumbnails for one that might match this variant's option
          for (const thumbnail of thumbnails) {
            const img = thumbnail.querySelector('img');
            const alt = img?.getAttribute('alt')?.toLowerCase() || '';
            const src = img?.getAttribute('src')?.toLowerCase() || '';

            // Check if image alt or src contains the variant option
            if (alt.includes(optionLower) || src.includes(optionLower)) {
              targetMediaId = thumbnail.getAttribute('data-target');
              log('Gallery %s: ✅ Matched variant option "%s" to thumbnail: %s',
                  galleryId, variantOption, targetMediaId);
              break;
            }
          }

          if (!targetMediaId) {
            log('Gallery %s: Option-based matching failed - "%s" not found in any thumbnail alt/src',
                galleryId, variantOption);
          }
        } else {
          log('Gallery %s: Option-based matching skipped - variant has no option1 value', galleryId);
        }
      }

      // Update the gallery if we found a target
      if (targetMediaId && setActiveMediaInGallery(gallery, targetMediaId)) {
        updated++;
        log('Gallery %s: Successfully updated to show variant image', galleryId);
      } else {
        log('Gallery %s: ❌ Could not find matching image for variant "%s"', galleryId, variant.title);
        log('Gallery %s: Available: %d media items, %d thumbnails',
            galleryId, allMediaItems.length, thumbnails.length);
      }
    });

    if (updated === 0) {
      console.warn('[Variant Fix] No product images were updated - could not match variant to image');
      log('Troubleshooting:');
      log('1. Check if variant has featured_image in Shopify admin');
      log('2. Verify variant position matches thumbnail order');
      log('3. Check if image filenames contain variant option names');
    } else {
      log(`✅ Updated ${updated}/${mediaGalleries.length} media galleries`);
    }

    return updated > 0;
  }

  // Update variant selection dropdowns based on a variant object
  function updateVariantSelectionFromVariant(variant) {
    if (!variant) return false;

    log('Updating variant selection to:', variant.title);

    const selects = document.querySelectorAll('select[name*="options"]');
    let updated = 0;

    // Update each select to match the variant's options
    selects.forEach((select, index) => {
      const optionValue = variant[`option${index + 1}`];
      if (optionValue && select.value !== optionValue) {
        select.value = optionValue;
        // Dispatch change event so other listeners know
        select.dispatchEvent(new Event('change', { bubbles: true }));
        log(`Updated select ${index} to:`, optionValue);
        updated++;
      }
    });

    // Update the form with variant ID
    updateVariantId(variant.id);

    return updated > 0;
  }

  // Make thumbnails clickable and functional
  function initializeThumbnailClicks() {
    log('Setting up thumbnail click handlers');

    const mediaGalleries = document.querySelectorAll('media-gallery');

    mediaGalleries.forEach(gallery => {
      const galleryId = gallery.id;

      // Find all thumbnail buttons in this gallery
      const thumbnails = gallery.querySelectorAll('.thumbnail-list__item button');

      thumbnails.forEach(thumbnail => {
        thumbnail.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();

          // Ignore if already active (avoid redundant work)
          if (this.getAttribute('aria-current') === 'true') {
            log('Thumbnail already active, ignoring click');
            return;
          }

          // Get the target media ID from the data-target attribute
          const targetId = this.closest('.thumbnail-list__item')?.dataset.target;

          if (!targetId) {
            log('WARNING: Thumbnail missing data-target attribute');
            return;
          }

          log('Thumbnail clicked for media:', targetId);

          // Extract media ID from target (e.g., "template--123-45678" -> 45678)
          const mediaIdMatch = targetId.match(/-(\d+)$/);
          if (mediaIdMatch && productData && productData.variants) {
            const mediaId = parseInt(mediaIdMatch[1], 10);

            // Check if this media belongs to a specific variant
            // Try multiple approaches to find the variant (matching the updateProductImages logic)
            let variantForMedia = null;

            // Approach 1: Check featured_image (standard Shopify property)
            variantForMedia = productData.variants.find(v => {
              const featuredImage = v.featured_image || v.featured_media;
              return featuredImage && featuredImage.id === mediaId;
            });

            // Approach 2: Check variant.image_id (set when images manually assigned)
            if (!variantForMedia) {
              variantForMedia = productData.variants.find(v =>
                v.image_id === mediaId
              );
            }

            // Approach 3: Check product.images[].variant_ids (reverse lookup)
            if (!variantForMedia && productData.images) {
              const imageData = productData.images.find(img => img.id === mediaId);
              if (imageData && imageData.variant_ids && imageData.variant_ids.length > 0) {
                // Get the first variant associated with this image
                variantForMedia = productData.variants.find(v =>
                  imageData.variant_ids.includes(v.id)
                );
              }
            }

            if (variantForMedia) {
              // This image is EXPLICITLY tied to a variant
              const currentOptions = getSelectedOptions();
              const currentVariant = findVariantByOptions(currentOptions);

              // Only update if clicking a DIFFERENT variant's featured image
              if (!currentVariant || currentVariant.id !== variantForMedia.id) {
                log('Thumbnail is for different variant - updating selection:', variantForMedia.title);
                updateVariantSelectionFromVariant(variantForMedia);

                // IMPORTANT: Return early - updateVariantSelectionFromVariant triggers
                // handleVariantChange which calls updateProductImages, so gallery
                // will be updated automatically. Don't update it again here.
                log('✅ Thumbnail click: variant updated to', variantForMedia.title);
                return;
              } else {
                log('Thumbnail is for currently selected variant - just updating gallery');
              }
            } else {
              log('Thumbnail is for shared/generic image - updating gallery only, not variant');
            }
          }

          // Update gallery visuals (for same-variant images and shared images)
          setActiveMediaInGallery(gallery, targetId);
        }, { capture: true });
      });

      log(`Initialized ${thumbnails.length} thumbnails in gallery:`, galleryId);
    });
  }

  // Make zoom/lightbox buttons functional
  function initializeZoomButtons() {
    log('Setting up zoom/lightbox handlers');

    // Find all modal-opener elements (theme's custom elements)
    const modalOpeners = document.querySelectorAll('modal-opener[data-modal]');
    let initialized = 0;

    modalOpeners.forEach(opener => {
      const modalId = opener.getAttribute('data-modal');
      const button = opener.querySelector('button[aria-haspopup="dialog"]');

      if (!button || !modalId) {
        return; // Skip if missing button or modal ID
      }

      button.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();

        log('Zoom button clicked, opening modal:', modalId);

        const modal = document.querySelector(modalId);

        if (!modal) {
          log('WARNING: Modal not found:', modalId);
          return;
        }

        // Open the modal
        modal.setAttribute('open', '');
        modal.classList.add('active');
        document.body.classList.add('overflow-hidden');

        // Focus the modal for accessibility
        const dialog = modal.querySelector('[role="dialog"]');
        if (dialog) {
          dialog.focus();
        }

        // Set up close button
        const closeButton = modal.querySelector('.product-media-modal__toggle, button[aria-label*="Close"], button[aria-label*="close"]');
        if (closeButton && !closeButton.__zoomCloseAttached) {
          closeButton.addEventListener('click', function(e) {
            e.preventDefault();
            modal.removeAttribute('open');
            modal.classList.remove('active');
            document.body.classList.remove('overflow-hidden');
            button.focus(); // Return focus to opener
            log('Modal closed');
          });
          closeButton.__zoomCloseAttached = true;
        }

        // Close on escape key
        if (!modal.__zoomEscapeAttached) {
          modal.addEventListener('keydown', function(e) {
            if (e.key === 'Escape') {
              modal.removeAttribute('open');
              modal.classList.remove('active');
              document.body.classList.remove('overflow-hidden');
              button.focus();
              log('Modal closed with Escape');
            }
          });
          modal.__zoomEscapeAttached = true;
        }

        // Close on backdrop click
        if (!modal.__zoomBackdropAttached) {
          modal.addEventListener('click', function(e) {
            if (e.target === modal) {
              modal.removeAttribute('open');
              modal.classList.remove('active');
              document.body.classList.remove('overflow-hidden');
              button.focus();
              log('Modal closed with backdrop click');
            }
          });
          modal.__zoomBackdropAttached = true;
        }

        log('✅ Modal opened:', modalId);
      }, { capture: true });

      initialized++;
    });

    log(`Initialized ${initialized} zoom/lightbox buttons`);
  }

  // Handle variant selection change
  function handleVariantChange() {
    if (!productData) {
      log('Product data not loaded yet, waiting...');
      return;
    }

    const selectedOptions = getSelectedOptions();
    const variant = findVariantByOptions(selectedOptions);

    if (variant) {
      // Update form immediately (critical for cart functionality)
      updateVariantId(variant.id);

      // Debounce image updates (visual only, can be delayed to prevent flickering)
      clearTimeout(imageUpdateTimeout);
      imageUpdateTimeout = setTimeout(() => {
        updateProductImages(variant);
      }, 50); // 50ms debounce - faster response while preventing thrashing
    } else {
      log('Could not find matching variant');
    }
  }

  // Initialize variant listeners
  function initializeListeners() {
    log('Setting up variant selection listeners');

    // Find all option selects
    const selects = document.querySelectorAll('select[name*="options"]');
    log('Found', selects.length, 'option select(s)');

    selects.forEach((select, i) => {
      log(`Attaching listener to select ${i}:`, select.name);

      select.addEventListener('change', function(e) {
        log('Select changed:', e.target.name, '=', e.target.value);
        handleVariantChange();
      }, { capture: true }); // Capture phase to run first
    });

    // Also listen for any radio buttons (in case theme uses them)
    const radios = document.querySelectorAll('input[type="radio"][name*="option"]');
    if (radios.length > 0) {
      log('Found', radios.length, 'option radio button(s)');
      radios.forEach(radio => {
        radio.addEventListener('change', function(e) {
          log('Radio changed:', e.target.name, '=', e.target.value);
          handleVariantChange();
        }, { capture: true });
      });
    }

    log('Listeners initialized');
  }

  // Check if we're on a product page
  function isProductPage() {
    const path = window.location.pathname;

    // Explicitly exclude known non-product page patterns
    const excludePatterns = [
      '/collections/',
      '/search',
      '/cart',
      '/account',
      '/pages/',
      '/blogs/',
      '/a/wishlist',
    ];

    if (excludePatterns.some(pattern => path.includes(pattern))) {
      log('Excluded page type detected:', path);
      return false;
    }

    // Check URL path first (fastest check)
    if (path.includes('/products/')) {
      return true;
    }

    // Check for product-info element (includes featured products)
    if (document.querySelector('product-info')) {
      return true;
    }

    // Backup check: Single cart form with product options
    // (Collection pages have multiple forms, product pages have one)
    const forms = document.querySelectorAll('form[action*="/cart/add"]');
    if (forms.length === 1) {
      // Verify it has product options
      const hasOptions = document.querySelectorAll('select[name*="options"]').length > 0;
      if (hasOptions) {
        log('Single cart form with options detected - treating as product page');
        return true;
      }
    }

    return false;
  }

  // Main initialization
  async function init() {
    // Prevent duplicate initialization
    if (window.__variantFixInitialized) {
      log('Already initialized, skipping');
      return;
    }

    // Only run on product pages
    if (!isProductPage()) {
      log('Not a product page, skipping variant fix');
      return;
    }

    log('Initializing variant fix');

    // Wait for product data to load with timeout
    const productDataPromise = fetchProductData();
    const timeoutPromise = new Promise((resolve) =>
      setTimeout(() => resolve(null), 5000) // 5 second timeout
    );

    await Promise.race([productDataPromise, timeoutPromise]);

    if (!productData) {
      console.warn('[Variant Fix] Could not load product data - falling back to theme default behavior');

      // If the URL strongly suggests this is a product page, show a user-visible warning
      if (window.location.pathname.includes('/products/')) {
        console.error('[Variant Fix] Product page detected but data fetch failed. Variant selection may not work correctly.');
      }
      return;
    }

    // Set up change listeners
    initializeListeners();

    // Set up thumbnail click handlers (theme's are broken)
    initializeThumbnailClicks();

    // Set up zoom/lightbox handlers (theme's are broken)
    initializeZoomButtons();

    window.__variantFixInitialized = true;
    log('Variant fix initialized successfully');
  }

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  log('=== Variant Fix Loaded ===');

})();
